/**
 * シーンノート（旧 REFLECTION。05 振り返り）
 *
 * 保存済みシーンの起点。上部の工程レールから測光・露出・現像へ戻れる。
 * - 見出し（撮影日・カメラ・題名）、写真
 * - 01 予測（自動）／02 現像（評価・メモ、現像タイマーを開く）／03 プリント（開閉）
 * - 撮影環境（天気・太陽・日の出入り・月・座標と地図）
 * - シーン情報（題名・場所・天気と光）
 * - 入力ごとに自動保存。下端「保存して閉じる」
 */

import {
  showScreen, resetScreen, escapeHtml, toast, zoneToRoman, icon, buzz, renderRail,
  formatDateTime, formatDate, formatClock, blobUrl, releaseUrls, chapterLink,
} from './ui.js';
import { getCurrentShoot, setCurrentShoot, updateCurrentShoot, flushSave, committedSpots } from './scene.js';
import { getShoot } from './store.js';
import { getFilmById } from './data/films.js';
import { getDeveloperById } from './data/developers.js';
import { formatSeconds } from './data/dev-times.js';
import { planLabel } from './zone-calc.js';
import { moonPhaseLabel, sunAltitudeLabel } from './environment.js';

const EXP = [['under', 'アンダー'], ['good', '適正'], ['over', 'オーバー']];
const CON = [['too-flat', '軟調すぎ'], ['as-planned', '計画どおり'], ['too-contrasty', '硬調すぎ']];
const RATINGS = [['shadowQuality', 'シャドウの質'], ['midtoneQuality', '中間調の質'], ['highlightQuality', 'ハイライトの質'], ['satisfaction', '総合満足度']];

let printOpen = false;

/** ライブラリ・ホームから：保存済みシーンを開く */
export async function openShootById(id) {
  const shoot = await getShoot(id);
  if (!shoot) { toast('シーンが見つかりません'); return; }
  setCurrentShoot(shoot);
  openReflection();
}

export function openReflection(opts = {}) {
  const shoot = getCurrentShoot();
  if (!shoot) { toast('シーンがありません'); showScreen('home', { back: true }); return; }
  printOpen = !!shoot.printing;
  render();
  showScreen('reflection', { back: !!opts.back });
}

function render() {
  const el = document.getElementById('screen-reflection');
  const shoot = getCurrentShoot();
  releaseUrls('note');
  const film = getFilmById(shoot.filmId);
  const spots = committedSpots(shoot);
  const sh = spots.find((s) => s.id === shoot.shadowAnchorId) || spots.find((s) => s.role === 'shadow-anchor');
  const hi = spots.find((s) => s.id === shoot.highlightAnchorId) || spots.find((s) => s.role === 'highlight-anchor');
  const facts = shoot.developmentPlan ? [
    ['フィルム · EI', `${film?.name || shoot.filmId} · EI ${shoot.ei}`],
    ['現像方針', planLabel(shoot.developmentPlan)],
    ['推奨露出', shoot.aperture ? `${shoot.aperture} @ ${shoot.shutterSpeed}` : '—'],
    ...(shoot.shotExposure ? [['撮影した露出', `${shoot.shotExposure.aperture} @ ${shoot.shotExposure.shutter}${shoot.shotExposure.bracketStops ? ` · ブラケット ±${{ 0.333: '⅓', 0.5: '½' }[shoot.shotExposure.bracketStops] || shoot.shotExposure.bracketStops} 段` : ''}`]] : []),
    ['ピン', String(spots.length)],
    ['ゾーン', sh ? (hi ? `${zoneToRoman(sh.placedZone)} → ${zoneToRoman(hi.placedZone)}` : zoneToRoman(sh.placedZone)) : '—'],
  ] : [
    ['フィルム · EI', `${film?.name || shoot.filmId} · EI ${shoot.ei}`],
    ['ピン', String(spots.length)],
    ['露出', '未計算'],
  ];

  el.innerHTML = `
    <div class="safe-top"></div>
    <div class="bar">
      <button class="bar__btn" type="button" data-close aria-label="保存して閉じる">${icon('close', 22)}</button>
      <div class="bar__title" style="margin-right:44px">シーンノート</div>
    </div>
    <div id="note-rail"></div>
    <div class="rail-hint">下線の工程をタップすると、その画面に戻れます</div>
    <div class="body" style="padding-bottom:24px">
      <div class="note-photo photo photo--develop" id="note-photo">${shoot.monoImageBlob ? `<img src="${blobUrl('note', shoot.monoImageBlob)}" alt="" />` : ''}</div>
      <div style="padding:16px var(--gutter) 0;animation:zn-up .5s .2s both">
        <div class="en sub" style="font-size:12px">${escapeHtml(`${formatDateTime(shoot.createdAt)} · ${shoot.camera || 'Camera'}`)}</div>
        <div style="font:500 26px/1.35 var(--font-jp-serif);margin-top:10px" id="note-title">${escapeHtml(shoot.title || '無題のシーン')}</div>
        <div class="sub" style="display:flex;align-items:center;gap:6px;font-size:11px;margin-top:10px"><svg class="i draw" width="14" height="14" style="animation-delay:.6s"><use href="#i-check"/></svg>自動保存</div>
      </div>

      <div class="block" style="margin-top:26px">
        <div class="block__head"><span class="block__title"><span class="en">01</span>予測</span><span class="badge is-on">撮影時 · 自動</span></div>
        <div class="facts">${facts.map(([k, v]) => `<span>${k}</span><span>${escapeHtml(v)}</span>`).join('')}</div>
        <button class="btn-rule" type="button" data-ask-scene style="margin-top:14px;width:100%">${icon('chat', 16)}このシーンを AI に相談</button>
      </div>

      <div class="block" id="note-dev"></div>
      <div class="block" id="note-print"></div>
      ${envHtml(shoot)}

      <div class="block">
        <span class="block__title">シーン情報</span>
        <label class="field"><span class="field__label">タイトル</span><input class="input" data-text="title" value="${escapeHtml(shoot.title || '')}" placeholder="例：朝霧・棚田" /></label>
        <label class="field"><span class="field__label">場所</span><input class="input" data-text="location" value="${escapeHtml(shoot.location || '')}" placeholder="例：鎌倉 / 奥多摩" /></label>
        <label class="field"><span class="field__label">天気・光</span><input class="input" data-text="weather" value="${escapeHtml(shoot.weather || '')}" placeholder="例：晴・午後 3 時の斜光" /></label>
      </div>
    </div>
    <div class="foot"><button class="btn-primary" type="button" data-close style="height:58px">保存して閉じる</button></div>
  `;

  renderRail(el.querySelector('#note-rail'), 4, {
    1: () => import('./mapping.js').then((m) => m.openMapping({ back: true })),
    2: () => import('./exposure.js').then((m) => m.openExposure({ back: true })),
    3: () => import('./developer.js').then((m) => m.openDevelopment({ back: true })),
  });
  requestAnimationFrame(() => requestAnimationFrame(() => el.querySelector('#note-photo')?.classList.add('is-developed')));

  el.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', async () => {
    try { await flushSave(); toast('保存しました'); } catch { toast('保存に失敗しました'); return; }
    resetScreen('home');
  }));
  el.querySelectorAll('[data-text]').forEach((inp) => inp.addEventListener('input', () => {
    updateCurrentShoot({ [inp.dataset.text]: inp.value.trim() });
    if (inp.dataset.text === 'title') el.querySelector('#note-title').textContent = inp.value.trim() || '無題のシーン';
  }));
  el.querySelector('[data-map]')?.addEventListener('click', () => openMap(shoot.gps));
  el.querySelector('[data-ask-scene]').addEventListener('click', () => {
    import('./ask.js').then((m) => m.openAsk({ scene: getCurrentShoot() }));
  });

  renderDev();
  renderPrint();
}

// --------------------------------------------------
// 02 現像
// --------------------------------------------------
function renderDev() {
  const box = document.getElementById('note-dev');
  const d = getCurrentShoot().development;
  const done = !!d?.exposureAccuracy;
  const badge = done ? '完了' : d ? '記録あり' : '未入力';
  let inner = '';
  if (d) {
    const dev = getDeveloperById(d.developer);
    inner = `
      <div class="en" style="font-size:13px;line-height:1.6;margin-top:12px">
        ${escapeHtml(`${formatDate(d.developedAt)} · ${dev ? `${dev.name} ${dev.dilution}` : d.developer || '—'}`)}<br>
        ${d.actualDevTime ? escapeHtml(`${formatSeconds(d.actualDevTime)} @ ${Number(d.tempC ?? 20).toFixed(1)}°C（目標 ${formatSeconds(d.targetDevTime || 0)}）`) : '手入力の記録'}
      </div>
      <div class="field__label" style="margin:16px 0 8px">露出の評価</div>
      ${segHtml('exp', EXP, d.exposureAccuracy)}
      <div class="field__label" style="margin:14px 0 8px">コントラスト</div>
      ${segHtml('con', CON, d.contrastResult)}
      <label class="field"><span class="field__label">現像メモ</span><textarea class="textarea" data-dev-notes placeholder="シャドウやハイライトの状態など">${escapeHtml(d.notes || '')}</textarea></label>`;
  } else {
    inner = `
      <div class="sub" style="font-size:13px;line-height:1.7;margin-top:12px">現像の記録がまだありません。タイマーで「完了・記録」すると自動で入ります。</div>
      <button class="btn-line btn-line--block" type="button" data-add-dev style="margin-top:12px;height:46px">現像結果を追記</button>`;
  }
  box.innerHTML = `
    <div class="block__head"><span class="block__title"><span class="en">02</span>現像</span><span class="badge ${done ? 'is-on' : ''}">${badge}</span></div>
    ${inner}
    <button class="btn-rule" type="button" data-open-dev style="margin-top:8px;width:100%">${icon('moon', 14)}現像タイマーを開く</button>
    <div style="display:flex;gap:4px 14px;flex-wrap:wrap;margin-top:6px">${chapterLink('troubleshooting', '28 トラブルシューティング')}</div>`;
  box.querySelector('[data-add-dev]')?.addEventListener('click', () => {
    const s = getCurrentShoot();
    updateCurrentShoot({
      development: {
        developedAt: Date.now(), developer: s.defaultDeveloperId, tempC: Number.isFinite(s.devTempC) ? s.devTempC : 20.0,
        targetDevTime: 0, actualDevTime: 0, exposureAccuracy: null, contrastResult: null, notes: '',
      },
    });
    renderDev();
  });
  bindSeg(box, 'exp', (v) => patchDev({ exposureAccuracy: v }));
  bindSeg(box, 'con', (v) => patchDev({ contrastResult: v }));
  box.querySelector('[data-dev-notes]')?.addEventListener('input', (e) => patchDev({ notes: e.target.value.trim() }, false));
  box.querySelector('[data-open-dev]').addEventListener('click', () => import('./developer.js').then((m) => m.openDevelopment()));
}

function patchDev(patch, repaint = true) {
  const d = getCurrentShoot().development || {};
  updateCurrentShoot({ development: { ...d, ...patch } });
  if (repaint) {
    const badge = document.querySelector('#note-dev .badge');
    const done = !!getCurrentShoot().development?.exposureAccuracy;
    if (badge) { badge.textContent = done ? '完了' : '記録あり'; badge.classList.toggle('is-on', done); }
  }
}

function segHtml(key, opts, val) {
  const idx = opts.findIndex(([v]) => v === val);
  return `<div class="seg seg--slide" data-seg="${key}" style="height:46px">
    <span class="seg__thumb" style="width:33.333%;left:${(Math.max(0, idx) * 100) / 3}%;opacity:${idx < 0 ? 0 : 1}"></span>
    ${opts.map(([v, t], i) => `<button type="button" class="seg__opt ${i === idx ? 'is-on' : ''}" data-v="${v}" style="font-size:13.5px">${t}</button>`).join('')}
  </div>`;
}

function bindSeg(root, key, onPick) {
  const seg = root.querySelector(`[data-seg="${key}"]`);
  if (!seg) return;
  const items = [...seg.querySelectorAll('[data-v]')];
  items.forEach((b, i) => b.addEventListener('click', () => {
    items.forEach((x) => x.classList.toggle('is-on', x === b));
    const th = seg.querySelector('.seg__thumb');
    th.style.left = `${(i * 100) / items.length}%`;
    th.style.opacity = '1';
    buzz(6);
    onPick(b.dataset.v);
  }));
}

// --------------------------------------------------
// 03 プリント
// --------------------------------------------------
function renderPrint() {
  const box = document.getElementById('note-print');
  const p = getCurrentShoot().printing;
  const done = p?.satisfaction != null;
  box.innerHTML = `
    <div class="block__head"><span class="block__title"><span class="en">03</span>プリント</span><span class="badge ${done ? 'is-on' : ''}">${done ? '完了' : p ? '記録あり' : '未入力'}</span></div>
    ${printOpen ? '' : `<button class="btn-line btn-line--block" type="button" data-open-print style="margin-top:14px;height:46px">${icon('plus', 16)}プリント結果を追記</button>`}
    <div class="collapse ${printOpen ? 'is-open' : ''}"><div>
      ${p ? `<div class="en sub" style="font-size:12px;margin-top:12px">${escapeHtml(formatDate(p.printedAt))}</div>` : ''}
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 10px">
        <label class="field" style="grid-column:1/-1"><span class="field__label">印画紙</span><input class="input en" data-p="paper" value="${escapeHtml(p?.paper || '')}" placeholder="例：Ilford MGFB Classic" /></label>
        <label class="field"><span class="field__label">号数</span><input class="input en" data-p="paperGrade" value="${escapeHtml(p?.paperGrade || '')}" placeholder="Grade 2.5 / Split 0+5" /></label>
        <label class="field"><span class="field__label">基本露光</span><input class="input en" data-p="baseExposure" value="${escapeHtml(p?.baseExposure || '')}" placeholder="12s @ f/8" /></label>
      </div>
      <label class="field"><span class="field__label">覆い焼き・焼き込み</span><textarea class="textarea" data-p="dodgeBurnNotes" placeholder="例：空 +4s 焼き込み、前景の岩 −2s 覆い焼き">${escapeHtml(p?.dodgeBurnNotes || '')}</textarea></label>
      <button class="btn-rule" type="button" data-dbmap style="margin-top:8px;width:100%">${icon('edit', 16)}覆い焼き・焼き込みの地図${p?.dodgeBurnMap?.length ? `（${p.dodgeBurnMap.length} か所）` : 'を描く'}</button>
      <div style="margin-top:16px;display:flex;flex-direction:column;gap:10px">
        ${RATINGS.map(([k, t]) => `<div class="rate"><span class="rate__t">${t}</span><div class="rate__cells">${[1, 2, 3, 4, 5].map((v) => `<button type="button" class="${p?.[k] === v ? 'is-on' : ''}" data-rate="${k}" data-v="${v}">${v}</button>`).join('')}</div></div>`).join('')}
      </div>
      <label class="field"><span class="field__label">学んだこと（次回へ）</span><textarea class="textarea" data-p="lessonLearned" placeholder="例：朝の斜光は思ったよりコントラストが高い">${escapeHtml(p?.lessonLearned || '')}</textarea></label>
      <div style="display:flex;gap:4px 14px;flex-wrap:wrap;margin-top:6px">${chapterLink('printing', '07 プリント')}${chapterLink('fine-printing', '22 ファインプリント')}</div>
    </div></div>`;
  box.querySelector('[data-open-print]')?.addEventListener('click', () => {
    if (!getCurrentShoot().printing) {
      updateCurrentShoot({
        printing: {
          printedAt: Date.now(), paper: '', paperGrade: '', baseExposure: '', dodgeBurnNotes: '',
          shadowQuality: null, midtoneQuality: null, highlightQuality: null, satisfaction: null, lessonLearned: '',
        },
      });
    }
    printOpen = true;
    renderPrint();
  });
  box.querySelector('[data-dbmap]')?.addEventListener('click', () => {
    import('./dodge-burn.js').then((m) => m.openDodgeBurnMap(() => renderPrint()));
  });
  box.querySelectorAll('[data-p]').forEach((inp) => inp.addEventListener('input', () => patchPrint({ [inp.dataset.p]: inp.value.trim() })));
  box.querySelectorAll('[data-rate]').forEach((b) => b.addEventListener('click', () => {
    patchPrint({ [b.dataset.rate]: Number(b.dataset.v) });
    box.querySelectorAll(`[data-rate="${b.dataset.rate}"]`).forEach((x) => x.classList.toggle('is-on', x === b));
    buzz(6);
    const badge = box.querySelector('.badge');
    const done = getCurrentShoot().printing?.satisfaction != null;
    badge.textContent = done ? '完了' : '記録あり';
    badge.classList.toggle('is-on', done);
  }));
}

function patchPrint(patch) {
  const p = getCurrentShoot().printing || {};
  updateCurrentShoot({ printing: { ...p, ...patch } });
}

// --------------------------------------------------
// 撮影環境
// --------------------------------------------------
function envHtml(shoot) {
  const env = shoot.environment;
  const gps = shoot.gps && Number.isFinite(shoot.gps.lat) ? shoot.gps : null;
  if (!env && !gps) {
    return `<div class="block"><div class="block__head"><span class="block__title">撮影環境</span><span class="eyebrow">auto</span></div>
      <p class="sub" style="font-size:12.5px;line-height:1.7;margin:12px 0 0">位置情報がないため記録していません。設定の「位置情報を記録」が on のとき、撮影時に天気・太陽・月を自動で記録します。</p></div>`;
  }
  const w = env?.weather;
  const cells = [
    ['天気', w ? `${escapeHtml(w.description || '—')} ${w.tempC != null ? `${w.tempC}°C` : ''}` : '—', w ? `湿度 ${w.humidity ?? '—'}% · 雲量 ${w.cloudCover ?? '—'}% · 風 ${w.windSpeed ?? '—'}km/h` : '取得できませんでした'],
    ['太陽', env?.sunAltitude != null ? `高度 ${env.sunAltitude}°` : '—', env?.sunAltitude != null ? `${sunAltitudeLabel(env.sunAltitude)} · 方位 ${env.sunAzimuth}°` : ''],
    ['日の出 · 南中 · 日の入り', env ? `${formatClock(env.sunrise)} · ${formatClock(env.solarNoon)} · ${formatClock(env.sunset)}` : '—', ''],
    ['月', env?.moonPhase != null ? moonPhaseLabel(env.moonPhase) : '—', env?.moonIllumination != null ? `照度 ${env.moonIllumination}%` : ''],
  ];
  return `<div class="block">
    <div class="block__head"><span class="block__title">撮影環境</span><span class="eyebrow">auto</span></div>
    <div class="env-grid">${cells.map(([k, v, s]) => `<div><div class="env-grid__k">${k}</div><div class="env-grid__v">${v}</div>${s ? `<div class="env-grid__s">${escapeHtml(s)}</div>` : ''}</div>`).join('')}</div>
    ${gps ? `<button class="btn-rule" type="button" data-map style="margin-top:8px;width:100%">${icon('map', 16)}<span class="en">${gps.lat.toFixed(4)}, ${gps.lon.toFixed(4)}</span> · 地図で開く</button>` : ''}
  </div>`;
}

export function openMap(gps) {
  if (!gps) return;
  const lat = Number(gps.lat);
  const lon = Number(gps.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
  window.open(`https://maps.apple.com/?ll=${lat},${lon}&q=Zone%20Notes%20shot`, '_blank', 'noopener');
}
